import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { useTheme, Box, Typography } from '@mui/material';
import { scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';

const BarMultiLinesChart = ({ 
  data = [], 
  title = "",
  bottomLegend = "X Axis", 
  leftLegend = "Y Axis", 
  isLinesVisible = true, 
  isHorizontalBottomLegend = false, 
  digit = 2,
  topOffSet = 0,
  bottomOffSet = 0,
  bottomLegendOffSet = 0, 
  itemWidthOffSet = 0,
  barTranslateXOffSet = 0,
  barTranslateYOffSet = 0,
  lineTranslateXOffSet = 0,
  lineTranslateYOffSet = 0,
  itemsSpacingOffSet = 0,
}) => {
  const theme = useTheme();
  const colorScale = scaleOrdinal(schemeCategory10);
  const primaryDark = theme.palette.primary.dark;
  const neutralDark = theme.palette.neutral.dark;
  const textPrimaryColor = theme.palette.text.primary;

  if (!data || data.length === 0 || !data[0]?.data?.length || !Array.isArray(data)) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>
        <Typography>Nothing to display</Typography>
      </Box>
    );
  }
  
  const [barValue, ...lineValues] = data;
  const indexBy = Object.keys(barValue.data[0])[0];
  const keys = [Object.keys(barValue.data[0])[1]];
  
  const formattedBarValue = barValue?.data?.map(e => ({
    ...e,
    y: e.y ? Number(e.y?.toFixed(digit)) : 0,
  }));
  
  const CustomLines = ({ xScale, yScale, bars }) => (
    <>
      {lineValues.map((line, index) => {
        if (!line.data.length) return null;
  
        const points = line?.data?.map(point => {
          const bar = bars.find(b => b.data.indexValue === point.x);
          const barCenter = bar ? bar.x + bar.width / 2 : xScale(point.x);
          return `${barCenter},${yScale(point.y)}`;
        });
  
        return (
          <React.Fragment key={index}>
            <polyline
              fill="none"
              stroke={line.color}
              strokeWidth={1}
              strokeDasharray="8 6"
              points={points.join(' ')}
            />
            {line?.data?.map((point, idx) => {
              if (!line.isAllPointsTextVisible && idx !== line.data.length - 1) return null;
  
              const bar = bars?.find(b => b.data.indexValue === point.x);
              const barCenter = bar ? bar.x + bar.width / 2 : xScale(point.x);

              return (
                <text
                  key={`${index}-${idx}`}
                  x={barCenter}
                  y={yScale(point.y) - 8}
                  fill={line.color}
                  fontSize={11}
                  textAnchor="middle"
                >
                  {point.y?.toFixed(digit)}
                </text>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
  
  const allYValues = data?.flatMap(d => d?.data?.map(d => d.y));  
  const yAxisMax = allYValues.length > 0 ? Math.max(...allYValues) * 1.1 : undefined; // Add 10% buffer
  
  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      gap={1} 
      sx={{ height: '100%', width: '100%' }}
    >
      {title && 
        <Typography 
          variant="h6" 
          align="center"
        >
          {title}
        </Typography>
      }

      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        <ResponsiveBar
          data={formattedBarValue}
          indexBy={indexBy}
          keys={keys}
          margin={{ 
            top: 25 + (topOffSet || 0), 
            right: 20, 
            bottom: 155 + (bottomOffSet || 0), 
            left: 80 
          }}
          padding={0.3}
          groupMode="grouped"
          valueScale={{ type: "linear", max: yAxisMax }}
          indexScale={{ type: "band", round: true }}
          colors={({ indexValue }) => colorScale(indexValue)}
          defs={[
            {
              id: "dots",
              type: "patternDots",
              background: "inherit",
              color: "#38bcb2",
              size: 4,
              padding: 1,
              stagger: true,
            },
            {
              id: "lines",
              type: "patternLines",
              background: "inherit",
              color: "#eed312",
              rotation: -45,
              lineWidth: 6,
              spacing: 10,
            },
          ]}
          borderColor={{ from: 'color', modifiers: [['darker', 1.5]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,          
            tickPadding: 45 + bottomLegendOffSet,
            tickRotation: isHorizontalBottomLegend ? 0 : 270,
            legend: bottomLegend,
            legendPosition: 'middle',
            legendOffset: 75 + bottomLegendOffSet,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: leftLegend || 'Value',
            legendPosition: 'middle',
            legendOffset: -60,
          }}
          enableTotals={true}
          totalsTextColor={textPrimaryColor}
          totalsOffset={8}
          enableLabel={true}
          labelTextColor={textPrimaryColor}
          labelSkipWidth={4}
          labelSkipHeight={4}
          labelPosition="end"
          labelOffset={8}
          animate={true}
          motionStiffness={90}
          motionDamping={15}  
          layers={[
            'grid', 'axes', 'bars', 'markers', 'legends', 
            isLinesVisible && ((props) => <CustomLines {...props} />)
          ].filter(Boolean)}        
          legends={[
            {
              data: [
                {
                  id: barValue.id,
                  label: barValue.id, 
                  color: barValue.color,
                }
              ], 
              anchor: 'top-right',
              direction: 'row',
              justify: false,
              translateX: -180 + barTranslateXOffSet,
              translateY: -25 + barTranslateYOffSet,
              itemWidth: 60 + itemWidthOffSet,
              itemHeight: 20,
              itemsSpacing: 2 + itemsSpacingOffSet,
              symbolSize: 12,
              symbolShape: 'circle',
              itemDirection: 'left-to-right',
              itemOpacity: 1,
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
            {
              data: lineValues.map((line) => ({
                id: line.id,
                label: line.id, 
                color: line.color,
              })),
              anchor: 'top-right',
              direction: 'row',
              justify: false,
              translateX: 5 + lineTranslateXOffSet,
              translateY: -25 + lineTranslateYOffSet,
              itemWidth: 60 + itemWidthOffSet,
              itemHeight: 20,
              itemsSpacing: 2 + itemsSpacingOffSet,
              symbolSize: 12,
              symbolShape: 'circle',
              itemDirection: 'left-to-right',
              itemOpacity: 1,
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}                        
          theme={{
            axis: {
              domain: {
                line: {
                  stroke: neutralDark,
                },
              },
              legend: {
                text: {
                  fill: textPrimaryColor,
                },
              },
              ticks: {
                line: {
                  stroke: neutralDark,
                  strokeWidth: 1,
                },
                text: {
                  fill: textPrimaryColor,
                },
              },
            },          
            legends: {
              text: {
                fill: textPrimaryColor,
              },
            }, 
            grid: {
              line: {
                stroke: neutralDark,
                strokeWidth: 0.2,
                strokeDasharray: '2 2'
              }
            },
            tooltip: {
              container: {
                background: theme.palette.neutral.light,
                color: textPrimaryColor,
                borderRadius: '4px',
                padding: '8px',
              },
            },
            labels: {
              text: {
                fill: textPrimaryColor,
                fontSize: '12px' 
              },
            },
            dots: {
              text: {
                fill: textPrimaryColor,
              },
            },
            totals: {
              text: {
                fill: primaryDark, 
                fontSize: '12px',
              },
            },
          }}      
        />
      </Box>
    </Box>
  );
};

export default BarMultiLinesChart;
